import React, { useState, useEffect, useRef } from 'react';
import { Container, Button, Row, Col } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import './AdminChoosePhoto.css';
import Image from "../../Pages/Images/hide_profile.webp";
import Navbar from "../../Pages/Navbar/AdminNavbar";
import axios from 'axios';
import Swal from 'sweetalert2';
import { useLocation, useNavigate } from 'react-router-dom';
import BaseUrl from '../../ApiServices/BaseUrl';
import { FaTrashAlt, FaEdit } from 'react-icons/fa';

// Dynamic face-api.js loading
let faceapiInstance = null;
let modelsLoadingPromise = null;

const loadFaceApi = async () => {
  if (faceapiInstance) return faceapiInstance;
  faceapiInstance = await import("face-api.js");
  return faceapiInstance;
};

const loadModels = async () => {
  if (modelsLoadingPromise) return modelsLoadingPromise;
  
  modelsLoadingPromise = (async () => {
    const faceapi = await loadFaceApi();
    try {
      // Load the correct tinyFaceDetector model
      await faceapi.nets.tinyFaceDetector.loadFromUri("/models");
      console.log("Face detection model loaded (Tiny)");
      return true;
    } catch (err) {
      console.error("Error loading face-api models:", err);
      return false;
    }
  })();
  
  return modelsLoadingPromise;
};

const AdminChoosePhoto = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user")) || {};
  const userId = location.state?.userId || user.id || '';

  const [showPhoto, setShowPhoto] = useState(true);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [previewImage, setPreviewImage] = useState(Image);
  const [originalImage, setOriginalImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraFileInputRef = useRef(null); // Separate ref for camera-only input

  // Models will be loaded on demand in validateFace
  useEffect(() => {
    // Pre-load models in the background when component mounts to reduce latency
    loadModels();

    if (userId) {
      fetchProfilePhoto();
    }

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [userId]);

  const fetchProfilePhoto = async () => {
    setIsFetching(true);
    try {
      const response = await axios.get(`${BaseUrl}/api/job-seeker/${userId}`);
      const data = response.data;

      let fullPhotoUrl = null;

      if (data.profile_photo) {
        fullPhotoUrl = `${BaseUrl}${data.profile_photo}`;
        setPreviewImage(fullPhotoUrl);
        setOriginalImage(fullPhotoUrl);
      }

      if (typeof data.show_profile_photo !== 'undefined') {
        setShowPhoto(!!data.show_profile_photo);
        if (!data.show_profile_photo) {
          setPreviewImage(Image);
        } else if (data.profile_photo) {
          setPreviewImage(fullPhotoUrl);
        }
      }
    } catch (error) {
      console.error('Error fetching profile photo:', error);
      if (error.response?.status !== 404) {
        Swal.fire({
          title: 'Error!',
          text: 'Failed to fetch profile photo',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    } finally {
      setIsFetching(false);
    }
  };

  // Face validation function
  const validateFace = async (imageUrl) => {
    try {
      const faceapi = await loadFaceApi();
      await loadModels(); // Ensure models are loaded

      const img = await faceapi.fetchImage(imageUrl);

      const options = new faceapi.TinyFaceDetectorOptions({
        inputSize: 416,        // larger input = more accurate
        scoreThreshold: 0.5    // confidence threshold
      });

      const detections = await faceapi.detectAllFaces(img, options);
      console.log("Detections:", detections); // debug
      return detections.length > 0;
    } catch (err) {
      console.error("Face detection error:", err);
      return false;
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Basic validation
    if (!file.type.match('image.*')) {
      Swal.fire({
        title: 'Error!',
        text: 'Please select an image file',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      Swal.fire({
        title: 'Error!',
        text: 'Image size should be less than 5MB',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Preview image first
    const reader = new FileReader();
    reader.onload = async (event) => {
      const imageUrl = event.target.result;

      // Validate if face exists
      const hasFace = await validateFace(imageUrl);
      if (!hasFace) {
        Swal.fire({
          title: 'Invalid Image!',
          text: 'Please upload a photo with a clear human face.',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
        return;
      }

      // If face exists, accept file
      setProfilePhoto(file);
      setPreviewImage(imageUrl);
      setOriginalImage(imageUrl);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadClick = () => {
    // Open file input WITHOUT capture attribute for file selection
    fileInputRef.current.click();
  };

  const handleClickPhoto = () => {
    // For mobile, we'll use the separate camera input
    if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      cameraFileInputRef.current.click();
    } else {
      // For desktop, open camera directly
      openCamera();
    }
  };

  const handleCameraFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Preview image first
    const reader = new FileReader();
    reader.onload = async (event) => {
      const imageUrl = event.target.result;

      // Validate if face exists
      const hasFace = await validateFace(imageUrl);
      if (!hasFace) {
        Swal.fire({
          title: 'Invalid Image!',
          html: `
      <div style="text-align: left;">
        <p style="margin-bottom: 15px; font-weight: bold;">Please take a photo with a clear human face.</p>
        <p style="margin-bottom: 10px;">To improve your photo:</p>         
        <ul style="text-align: left; padding-left: 20px; margin-bottom: 0;">
          <li>📸 Ensure your face is clearly visible and centered</li>
          <li>💡 Take photo under good lighting - avoid backlight or shadows</li>
          <li>📱 Hold the camera steady and look directly into it</li>
          <li>😊 Make sure your entire face is in the frame</li>
        </ul>
      </div>
    `,
          icon: 'warning',
          confirmButtonText: 'OK',
          width: '500px'
        });
        return;
      }

      setProfilePhoto(file);
      setPreviewImage(imageUrl);
      setOriginalImage(imageUrl);
    };
    reader.readAsDataURL(file);
  };

  const openCamera = async () => {
    try {
      setIsCameraOpen(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      });
      streamRef.current = stream;
      videoRef.current.srcObject = stream;
    } catch (err) {
      console.error("Error accessing camera:", err);
      Swal.fire({
        title: 'Error!',
        text: 'Could not access camera. Please check permissions.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      setIsCameraOpen(false);
    }
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext('2d');
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(async (blob) => {
      if (blob) {
        const file = new File([blob], 'profile-photo.jpg', { type: 'image/jpeg' });
        const imageUrl = URL.createObjectURL(blob);

        // Validate face before accepting
        const hasFace = await validateFace(imageUrl);
        if (!hasFace) {
          Swal.fire({
            title: 'Invalid Image!',
            html: `
      <div style="text-align: left;">
        <p style="margin-bottom: 15px; font-weight: bold;">Please take a photo with a clear human face.</p>
        <p style="margin-bottom: 10px;">To improve your photo:</p>         
        <ul style="text-align: left; padding-left: 20px; margin-bottom: 0;">
          <li>📸 Ensure your face is clearly visible and centered</li>
          <li>💡 Take photo under good lighting - avoid backlight or shadows</li>
          <li>📱 Hold the camera steady and look directly into it</li>
          <li>😊 Make sure your entire face is in the frame</li>
        </ul>
      </div>
    `,
            icon: 'warning',
            confirmButtonText: 'OK',
            width: '500px'
          });
          return;
        }

        setProfilePhoto(file);
        setPreviewImage(imageUrl);
        setOriginalImage(imageUrl);
        closeCamera();
      }
    }, 'image/jpeg', 0.9);
  };

  const closeCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsCameraOpen(false);
  };

  const handleRadioChange = async (show) => {
    if (!show) {
      const result = await Swal.fire({
        html: '<div style="font-weight: bold;">Visible profile photos get more responses. Are you sure to hide your profile?</div>',
        icon: 'info',
        confirmButtonText: 'Yes, Hide',
        cancelButtonText: 'Keep Visible',
        showCancelButton: true,
        showCloseButton: true,
        customClass: {
          popup: 'small-swal',
          title: 'swal-title-small',
          icon: 'swal-icon-small',
          icon: 'custom-icon-class' // Add this
        },
        width: '350px',
        padding: '0.8rem'
      });

      if (result.isConfirmed) {
        setShowPhoto(false);
        setPreviewImage(Image);
      }
    } else {
      setShowPhoto(true);
      setPreviewImage(originalImage || Image);
    }
  };

  const handleDeletePhoto = async () => {
    if (!userId) {
      Swal.fire({
        title: 'Error!',
        text: 'User ID is missing',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This will delete your profile photo permanently!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        setIsLoading(true);
        const response = await axios.delete(`${BaseUrl}/api/job-seeker/${userId}/photo`, {
          data: {
            profile_photo: originalImage?.replace(`${BaseUrl}`, ''),
            show_profile_photo: showPhoto ? 1 : 0
          }
        });

        if (response.status === 200) {
          setPreviewImage(Image);
          setOriginalImage(null);
          setProfilePhoto(null);
          Swal.fire(
            'Deleted!',
            'Your profile photo has been deleted.',
            'success'
          );
        }
      } catch (error) {
        console.error('Error deleting profile photo:', error);
        Swal.fire({
          title: 'Error!',
          text: 'Failed to delete profile photo',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSave = async () => {
    if (!userId) {
      Swal.fire({
        title: 'Error!',
        text: 'User ID is missing',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      return;
    }

    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append('user_id', userId);
      formData.append('show_profile_photo', showPhoto ? 1 : 0);

      if (profilePhoto) {
        formData.append('profile_photo', profilePhoto);
      }

      const response = await axios.post(`${BaseUrl}/api/job-seeker`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      Swal.fire({
        title: 'Success!',
        text: 'Profile submitted successfully.', icon: 'success',
        confirmButtonText: 'OK'
      }).then((result) => {
        navigate('/manageemployee');
      });

      await fetchProfilePhoto();
    } catch (error) {
      console.error('Error saving profile photo:', error);
      Swal.fire({
        title: 'Error!',
        text: error.response?.data?.message || 'Failed to save profile photo',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <Container className="choose-photo-container py-4" style={{ marginTop: "55px" }}>
        <h1 className="text-center fw-bold mb-3">
          Choose Photo<span className="text-danger">*</span>
        </h1>

        <div className="form-wrapper mx-auto">
          {isCameraOpen ? (
            <div className="camera-container">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                className="camera-preview"
                style={{ transform: 'scaleX(1)' }} // Remove mirror effect
              ></video>
              <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
              <div className="camera-controls">
                <Button
                  className="btn-capture"
                  onClick={capturePhoto}
                  variant="primary"
                >
                  Capture Photo
                </Button>
                <Button
                  className="btn-cancel"
                  variant="danger"
                  onClick={closeCamera}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="photo-frame position-relative mx-auto">
                {isFetching ? (
                  <div className="text-center py-5">Loading profile photo...</div>
                ) : (
                  <>
                    <img
                      src={previewImage}
                      alt="Profile"
                      className="admin-preview-image"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = Image;
                      }}
                    />
                    {previewImage && previewImage !== Image && (
                      <div className="photo-actions">
                        <span
                          className="action-text text-danger fw-bold fs-5"
                          onClick={handleDeletePhoto}
                          style={{ cursor: "pointer" }}
                        >
                          Delete
                        </span>
                        <span
                          className="action-text text-primary fw-bold fs-5"
                          onClick={handleUploadClick}
                          style={{ cursor: "pointer" }}
                        >
                          Update
                        </span>
                      </div>
                    )}
                  </>
                )}

                {/* Main file input for UPLOAD - without capture attribute */}
                <input
                  type="file"
                  id="photo-upload"
                  ref={fileInputRef}
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                // Removed capture attribute to allow file selection on mobile
                />

                {/* Separate file input for CAMERA ONLY - with capture attribute */}
                <input
                  type="file"
                  id="camera-upload"
                  ref={cameraFileInputRef}
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleCameraFileChange}
                  capture="user" // This will force camera on mobile
                />
              </div>

              <div className="d-flex justify-content-between mt-3">
                <Button
                  className="btn-custom text-center"
                  onClick={handleUploadClick}
                  disabled={isFetching}
                >
                  Upload Photo
                </Button>
                <Button
                  className="btn-custom text-center"
                  onClick={handleClickPhoto}
                  disabled={isFetching}
                >
                  Click Photo
                </Button>
              </div>
            </>
          )}

          {!isCameraOpen && (
            <div className="radio-options text-center mt-3 d-flex justify-content-center align-items-center">
              <label className="custom-radio me-4 mb-0">
                <input
                  type="radio"
                  name="photoVisibility"
                  checked={showPhoto}
                  onChange={() => handleRadioChange(true)}
                  disabled={isFetching}
                />
                <span className="checkmark"></span>
                <span className="radio-label-text">Show Profile Photo</span>
              </label>
              <label className="custom-radio mb-0">
                <input
                  type="radio"
                  name="photoVisibility"
                  checked={!showPhoto}
                  onChange={() => handleRadioChange(false)}
                  disabled={isFetching}
                />
                <span className="checkmark"></span>
                <span className="radio-label-text">Hide Profile Photo</span>
              </label>
            </div>
          )}

            <small className="text-dark fw-bold">
              📸 <strong>Note:</strong> Profiles with photos receive more responses from employers
            </small>

          {!isCameraOpen && (
            <>
              <div className="text-center mt-4 pt-3 border-top d-flex justify-content-between gap-3">
                <button
                  className="nav-btn"
                  style={{
                    flex: 1,
                    backgroundColor: "#6c757d",
                    color: "white",
                    padding: "10px 20px",
                    border: "none",
                    borderRadius: "4px",
                    fontWeight: "bold"
                  }}
                  onClick={() => navigate('/adminuploadvideo', { state: { userId } })}
                >
                  Back
                </button>
                <button
                  className="btn-done" style={{ marginTop: '0px', flex: 1 }}
                  onClick={handleSave}
                  disabled={isLoading || isFetching}
                >
                  {isLoading ? 'Saving...' : (
                    <>
                      <span className="checkmark done-checkmark me-2"></span>Done
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </Container>
    </>
  );
};

export default AdminChoosePhoto;